// Reactive app state, all via @preact/signals. Components subscribe by
// reading `.value` inside their render — re-renders happen automatically.
//
// The `regionKey` signal is seeded from the URL hash on boot so the
// embed URL https://ceny-paliw.../#region=canarias opens directly in
// the Canary-Islands view (used by the wyspykanaryjskie.info embed).

import { computed, signal } from "@preact/signals";

import { inBbox } from "./geo";
import { REGIONS, findRegion } from "./regions";
import type { Bbox, Fuel, Station } from "./types";

// Per-site default region when no #region= is present in the URL.
// Canary-focused portals (PL/DE/FR) start on the archipelago; EN audience
// is broader Spain-travel, and embed/widget stay on the all-Spain view.
// Must be declared before regionKey signal so detectRegion() can read it
// at module-init time (otherwise: TDZ ReferenceError on boot).
const DEFAULT_REGION_BY_SITE: Record<string, string> = {
  pl: "canarias",
  de: "canarias",
  fr: "canarias",
};

export const fuel = signal<Fuel>("95");
export const regionKey = signal<string>(detectRegion());
export const viewport = signal<Bbox | null>(null);
export const stations = signal<Station[]>([]);
export const mapMode = signal<"split" | "full">("split");
// Mobile-only bottom-sheet expand state — tap on the station-list header
// flips this and the list overlays the map. CSS rule is media-query-scoped
// so desktop ignores it.
export const listExpanded = signal<boolean>(false);
export const selectedStationId = signal<string | null>(null);
export const userLocation = signal<{ lat: number; lng: number } | null>(null);
export const loading = signal<boolean>(true);
export const loadError = signal<string | null>(null);

/** Update regionKey and persist the choice to the URL hash. */
export function setRegion(next: string): void {
  if (!findRegion(next)) return; // silently ignore unknown keys
  regionKey.value = next;
  try {
    const params = new URLSearchParams(location.hash.replace(/^#/, ""));
    params.set("region", next);
    history.replaceState(null, "", `#${params.toString()}`);
  } catch {
    /* history API may be unavailable in some iframe sandboxes */
  }
}

function detectRegion(): string {
  try {
    const params = new URLSearchParams(location.hash.replace(/^#/, ""));
    const fromHash = params.get("region");
    if (fromHash && findRegion(fromHash)) return fromHash;
  } catch {
    /* location may be unavailable during SSR/tests; fall through */
  }
  return DEFAULT_REGION_BY_SITE[__SITE__] ?? "all";
}

const filteredByRegion = computed<Station[]>(() => {
  const region = findRegion(regionKey.value) ?? REGIONS[0];
  if (region.key === "all") return stations.value;
  if (region.provinces) {
    const provSet = new Set(region.provinces);
    return stations.value.filter((s) => provSet.has(s.province));
  }
  if (region.bbox) {
    return stations.value.filter((s) => inBbox(s, region.bbox!));
  }
  return stations.value;
});

/** Stations visible given current region + fuel + map viewport, cheapest first. */
export const visibleStations = computed<Station[]>(() => {
  const f = fuel.value;
  const v = viewport.value;
  return filteredByRegion.value
    .filter((s) => s.prices[f] != null)
    .filter((s) => (v ? inBbox(s, v) : true))
    .sort((a, b) => (a.prices[f] ?? Infinity) - (b.prices[f] ?? Infinity));
});

/** Cheapest 5 stations in the current viewport — backs the list panel. */
export const top5 = computed<Station[]>(() => visibleStations.value.slice(0, 5));
